import React from "react";
import { connect } from "react-redux";
import { scrollIntoView } from "../../constants/utilityFunctions";

// HOC for applying styles to class component
import { withStyles } from "@material-ui/core/styles";

// MUI Components
import Typography from "@material-ui/core/Typography";
import ListItem from "@material-ui/core/ListItem";
import TextField from "@material-ui/core/TextField";
import Collapse from "@material-ui/core/Collapse";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import Card from "@material-ui/core/Card";
import MobileStepper from "@material-ui/core/MobileStepper";
import Button from "@material-ui/core/Button";
import KeyboardArrowLeft from "@material-ui/icons/KeyboardArrowLeft";
import KeyboardArrowRight from "@material-ui/icons/KeyboardArrowRight";
import IconButton from "@material-ui/core/IconButton";
// Other
import Sample from "./Sample";

// Icons
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import InfoIcon from "@material-ui/icons/Info";

// Actions
import {
  setSampleTrianglePosition,
  setMainViewSection,
  setFocusElement,
  setSelectedSample,
} from "../../actions";
import { colors } from "../../constants/colors";
import { customTheme } from "../../constants/customTheme";

const styles = {
  root: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    flex: "1 1 auto",
    minHeight: 0,
    overflow: "hidden",
  },
  locationName: {
    flex: "0 0 auto",
    padding: 20,
    boxShadow: (props) =>
      `0px 0px 10px 0px ${colors[props.theme].boxShadowColor}, 0.2)`,
  },
  samplesContainer: (props) => ({
    flex: "1 1 auto",
    marginLeft: "26px",
    minHeight: 0,
    overflowY: "auto",
    position: "sticky",
    ...customTheme().customScrollBar,
  }),
  triangle: (props) => ({
    ...customTheme().triangle,
    borderColor: `transparent ${
      colors[props.theme].bkg3
    } transparent transparent`,
    top: 0,
  }),
  right: {
    flex: "1 0 auto",
    display: "flex",
    flexDirection: "row-reverse",
    alignItems: "center",
    justifyContent: "space-between",
    marginRight: "15px",
  },
  icon: customTheme().iconMedium,
  collapseButton: customTheme().collapseButton,
  collapse: {
    marginBottom: "20px",
  },
  listItem: {
    height: "48px",
    borderTopLeftRadius: "4px",
    borderBottomLeftRadius: "4px",
    paddingLeft: "10px",
    "&.Mui-selected": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
  },
  listItemHover: {
    height: "48px",
    borderTopLeftRadius: "4px",
    borderBottomLeftRadius: "4px",
    paddingLeft: "10px",
    "&.Mui-selected": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
    "&.MuiListItem-button:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.16)",
    },
  },
  listItemOpen: {
    height: "48px",
    borderTopLeftRadius: "4px",
    paddingLeft: "10px",
    backgroundColor: (props) => colors[props.theme].locationInfo,
    borderBottomLeftRadius: "0px",
    "&.Mui-selected": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
    "&.MuiListItem-button": {
      backgroundColor: (props) => colors[props.theme].locationInfo,
      "&:hover": {
        backgroundColor: (props) => colors[props.theme].locationInfo,
      },
    },
  },
  locationInfoExpansion: {
    boxShadow: (props) =>
      props.theme === "light"
        ? `0px 4px 4px 0px ${colors[props.theme].boxShadowColor},0.2)`
        : `${colors[props.theme].boxShadowColor}, 0.2) 0px 2px 4px 0px`,
    backgroundColor: (props) => colors[props.theme].locationInfo,
    padding: "5px",
    borderTopLeftRadius: "0px",
    borderTopRightRadius: "0px",
  },
  locationInfoTitle: {
    fontSize: "1.5rem",
    fontWeight: 600,
    marginRight: "20px",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  locationInfoItem: {
    fontSize: "0.875rem",
    margin: "10px",
    overflow: "hidden",
    "& span": {
      textDecoration: "underline",
      fontWeight: "600",
    },
  },
  acqInstructionContainer: {
    display: "flex",
    flexDirection: "column",
  },
  acqInstructionImage: {
    maxWidth: "50%",
    maxHeight: "50%",
  },
  skinScoreContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "baseline",
  },
  skinScoreTextfield: {
    width: "40px",
    fontSize: "0.875rem",
    marginLeft: "5px",
    "& .MuiInputBase-root": {
      "& .MuiInputBase-input": {
        paddingBottom: "0px",
      },
    },
  },
  illustrationContainer: {
    width: "80%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    alignSelf: "center",
  },
  illustrationStepper: {
    width: "60%",
    backgroundColor: (props) => colors[props.theme].locationInfo,
  },
};

// Responsible for iterating over samples to generate scrollable sample list
// and for managing the sample selection 'triangle'
class SamplesList extends React.Component {
  constructor(props) {
    super(props);
    this.samplesContainer = React.createRef();
    this.triangle = React.createRef();
    this.state = {
      collapseButtonHover: false,
      locationInfoOpen: this.props.locationSampleIds.length === 0, //default to expanding location info if no samples are acquired at the location
      activeStep: 0, //index of the acq instruction illustration image to be shown
    };
  }

  componentDidMount = () => {
    // console.log('componentDidMount')
    this.handleTriangleHideShow();
  };

  componentDidUpdate = (prevProps) => {
    if (
      (prevProps.selectedLocationId !== this.props.selectedLocationId &&
        this.props.locationSampleIds.length === 0) ||
      (prevProps.rightView !== this.props.rightView &&
        !this.props.rightView &&
        !this.state.locationInfoOpen &&
        this.props.locationSampleIds.length === 0)
    ) {
      this.setState({
        ...this.state,
        activeStep:
          prevProps.selectedLocationId === this.props.selectedLocationId
            ? this.state.activeStep
            : 0,
        locationInfoOpen: true, //default to expanding location info if no samples are acquired at the lcoation
      });
    } else if (
      (prevProps.selectedLocationId !== this.props.selectedLocationId &&
        this.props.locationSampleIds.length !== 0) ||
      (this.props.rightView && this.state.locationInfoOpen)
    ) {
      this.setState({
        ...this.state,
        activeStep:
          prevProps.selectedLocationId === this.props.selectedLocationId
            ? this.state.activeStep
            : 0,
        locationInfoOpen: false,
      });
    } else if (prevProps.selectedLocationId !== this.props.selectedLocationId) {
      this.setState({ ...this.state, activeStep: 0 });
    }

    setTimeout(() => {
      this.handleTriangleHideShow();
    }, 0);
  };

  handleTriangleHideShow = () => {
    if (
      // if no sample selected && triangle not hidden
      this.props.selectedSampleId === null &&
      this.props.trianglePositionY !== -20
    ) {
      // hide triangle selector
      this.props.dispatch(setSampleTrianglePosition(-20));

      // if a sample is selected
    } else if (this.props.selectedSampleId !== null) {
      // Show triangle
      this.moveTriangleToSelectedSample();
    }
  };

  // moves triangle to currently selected position
  moveTriangleToSelectedSample = () => {
    // console.log('move triangle to selected sample')
    // find currently selected DOM node programatically
    // use ref for location list to narrow search - then target selected class
    const selected = this.samplesContainer.current.getElementsByClassName(
      "Mui-selected"
    );
    if (selected.length > 0) {
      // move selector to DOM node
      const offsetTop = selected[0].offsetTop;
      const height = selected[0].clientHeight;
      const newTrianglePosition = offsetTop + height / 2;
      // only update if value needs to change
      // otherwise in an infinite loop as changes to state trigger a rerender
      // and rerenders trigger useEffect()

      if (newTrianglePosition !== this.props.trianglePositionY) {
        this.props.dispatch(setSampleTrianglePosition(newTrianglePosition));
      }
      scrollIntoView(selected[0], this.samplesContainer.current);
    }
  };

  handleLocationInfoClick = () => {
    this.props.dispatch(setMainViewSection("locationInfoFocused"));
    this.setState({ ...this.state, locationInfoOpen: false });
    this.props.dispatch(setSelectedSample("SAMPLE_COLUMN"));
    this.props.dispatch(setFocusElement("SAMPLE_COLUMN"));
  };

  handleRotateIllustration = (event, direction) => {
    event.stopPropagation();
    if (direction === "NEXT") {
      this.setState({ ...this.state, activeStep: this.state.activeStep + 1 });
    } else if (direction === "BACK") {
      this.setState({ ...this.state, activeStep: this.state.activeStep - 1 });
    }
  };

  render() {
    const { classes } = this.props;

    return (
      <div className={classes.root}>
        <div className={classes.samplesContainer} ref={this.samplesContainer}>
          <ListItem
            button
            selected={this.props.selectedSampleId === "SAMPLE_COLUMN"}
            className={
              this.state.locationInfoOpen
                ? classes.listItemOpen
                : this.state.collapseButtonHover
                ? classes.listItem
                : classes.listItemHover
            }
            onClick={() => this.handleLocationInfoClick()}
            style={{
              boxShadow: this.props.focused
                ? `inset 0px -2px 0px 0px ${(props) =>
                    colors[props.theme].primary}`
                : "0px 0px 0px 0px",
            }}
          >
            <ListItemIcon>
              <InfoIcon className={classes.icon} />
            </ListItemIcon>
            <Typography className={classes.locationInfoTitle}>
              {this.props.locationName}
            </Typography>
            {!this.props.rightView && (
              <IconButton
                disableRipple
                className={classes.collapseButton}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseEnter={() =>
                  this.setState({
                    ...this.state,
                    collapseButtonHover: true,
                  })
                }
                onMouseLeave={() =>
                  this.setState({
                    ...this.state,
                    collapseButtonHover: false,
                  })
                }
                onClick={(e) => {
                  e.stopPropagation();
                  this.setState({
                    ...this.state,
                    locationInfoOpen: !this.state.locationInfoOpen,
                  });
                }}
              >
                {this.state.locationInfoOpen ? (
                  <KeyboardArrowUpIcon className={classes.icon} />
                ) : (
                  <KeyboardArrowDownIcon className={classes.icon} />
                )}
              </IconButton>
            )}
          </ListItem>
          <Collapse
            in={this.state.locationInfoOpen}
            timeout="auto"
            unmountOnExit
            className={classes.collapse}
          >
            <Card className={classes.locationInfoExpansion}>
              {this.props.notes && (
                <Typography className={classes.locationInfoItem}>
                  <span>Notes:</span> {this.props.notes}
                </Typography>
              )}
              {this.props.skinScore && (
                <div className={classes.skinScoreContainer}>
                  <Typography className={classes.locationInfoItem}>
                    <span>Skin Score:</span>
                  </Typography>
                  <TextField
                    className={classes.skinScoreTextfield}
                    value={this.props.skinScore}
                    disabled={true}
                  />
                </div>
              )}
              <div className={classes.acqInstructionContainer}>
                <Typography className={classes.locationInfoItem}>
                  <span>Acquisition instructions:</span>{" "}
                  {this.props.locInstruction.text
                    ? this.props.locInstruction.text
                    : "No instructions"}
                </Typography>

                {this.props.locInstruction.illustration.length !== 0 &&
                  this.props.locInstruction.illustration[
                    this.state.activeStep
                  ] && (
                    <div className={classes.illustrationContainer}>
                      <img
                        alt={"acquisition instruction illustration"}
                        className={classes.acqInstructionImage}
                        src={
                          this.props.locInstruction.illustration[
                            this.state.activeStep
                          ].image
                        }
                      />
                      {this.props.locInstruction.illustration.length > 1 && (
                        <MobileStepper
                          variant="dots"
                          steps={this.props.locInstruction.illustration.length}
                          position="static"
                          activeStep={this.state.activeStep}
                          className={classes.illustrationStepper}
                          nextButton={
                            <Button
                              size="small"
                              onClick={(e) =>
                                this.handleRotateIllustration(e, "NEXT")
                              }
                              disabled={
                                this.state.activeStep ===
                                this.props.locInstruction.illustration.length -
                                  1
                              }
                            >
                              Next
                              <KeyboardArrowRight />
                            </Button>
                          }
                          backButton={
                            <Button
                              size="small"
                              onClick={(e) =>
                                this.handleRotateIllustration(e, "BACK")
                              }
                              disabled={this.state.activeStep === 0}
                            >
                              <KeyboardArrowLeft />
                              Back
                            </Button>
                          }
                        />
                      )}
                    </div>
                  )}
              </div>
            </Card>
          </Collapse>
          {this.props.locationSampleIds.length > 0 &&
            // Render samples list
            this.props.locationSampleIds.map((id, index) => (
              <Sample key={index} id={id} />
            ))}
          {/* Triangle Selector */}
          <div
            className={classes.triangle}
            style={{
              top: this.props.trianglePositionY,
            }}
            ref={this.triangle}
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    selectedLocationId: state.locations.selectedLocationId,
    locationName: state.locations.byId[state.locations.selectedLocationId].name,
    notes: state.locations.byId[state.locations.selectedLocationId].notes,
    locationSampleIds:
      state.locations.byId[state.locations.selectedLocationId].samples, // array if sample ids for the selected location
    selectedSampleId: state.samples.selectedSampleId, // will change on every sample selection change
    trianglePositionY: state.samples.trianglePositionY,
    focused: state.utility.focusElement === "SAMPLE_COLUMN", // boolean to indicate focus underline
    rightView: state.view.mainViewSection.orientation === "RIGHT",
    skinScore:
      state.locations.byId[state.locations.selectedLocationId].skinScore,
    locInstruction:
      state.protocol.locations[state.locations.selectedLocationId].instruction,
    theme: state.utility.theme,
  };
};

export default connect(mapStateToProps)(
  withStyles(styles)(React.memo(SamplesList))
);
